"use client";

import React, { useCallback, useEffect, useState } from "react";
import DropdownSelectOne from "../../../components/custom/dropdown-select-one";
import AdjustmentRequestService, {
  CountingAdjustmentRequest,
  DebtAdjustmentRequest,
  ADJUSTMENT_REQUEST_STATUS,
  ADJUSTMENT_REQUEST_STATUS_LABEL,
  STAGE_LABEL,
  DEBT_REQUEST_TYPE_LABEL,
} from "../services/adjustment-request-service";
import { formatDateTime } from "../../fault-ticket/services/fault-ticket-service";
import CountingAdjustmentDetailModal from "../components/counting-adjustment-detail-modal";
import DebtAdjustmentDetailModal from "../components/debt-adjustment-detail-modal";
import EmptyState from "../../../components/custom/empty-state";

const STATUS_BADGE_STYLES: Record<number, string> = {
  [ADJUSTMENT_REQUEST_STATUS.PENDING]: "bg-amber-100 text-amber-700",
  [ADJUSTMENT_REQUEST_STATUS.APPROVED]: "bg-green-100 text-green-700",
  [ADJUSTMENT_REQUEST_STATUS.REJECTED]: "bg-slate-200 text-slate-600",
};

const DEBT_TYPE_BADGE_STYLES: Record<string, string> = {
  balance: "bg-violet-100 text-violet-700",
  backfill: "bg-sky-100 text-sky-700",
  debt: "bg-slate-100 text-slate-600",
};

type Tab = "counting" | "debt";

/**
 * Hàng đợi "Yêu cầu điều chỉnh" — NV đề xuất trên app, giám sát duyệt/từ chối ở đây.
 * 2 loại tách tab: SỐ LƯỢNG KIỂM ĐẾM (đếm nhầm / mất đồ tìm lại được) và CÔNG NỢ ĐỒ.
 */
const AdjustmentRequestIndexPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>("counting");
  const [status, setStatus] = useState<number | "all">(ADJUSTMENT_REQUEST_STATUS.PENDING);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const { fetch: fetchCounting } = AdjustmentRequestService.listCounting();
  const { fetch: fetchDebt } = AdjustmentRequestService.listDebt();

  const [countingData, setCountingData] = useState<{ list: CountingAdjustmentRequest[]; total_record: number } | null>(null);
  const [debtData, setDebtData] = useState<{ list: DebtAdjustmentRequest[]; total_record: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedCounting, setSelectedCounting] = useState<CountingAdjustmentRequest | null>(null);
  const [selectedDebt, setSelectedDebt] = useState<DebtAdjustmentRequest | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: status === "all" ? undefined : Number(status), page, limit };
      if (tab === "counting") {
        const res = await fetchCounting(params);
        if (res?.data) setCountingData(res.data as any);
      } else {
        const res = await fetchDebt(params);
        if (res?.data) setDebtData(res.data as any);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, page, limit]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setPage(1);
  }, [tab, status, limit]);

  const rows = tab === "counting" ? countingData?.list ?? [] : debtData?.list ?? [];
  const totalRecords = (tab === "counting" ? countingData?.total_record : debtData?.total_record) ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
  // counting: STT/Đơn/Mốc/Lý do/Trạng thái/Ngày tạo/Thao tác = 7. debt: STT/Khách hàng/Loại đồ/Số lượng/Lý do/Trạng thái/Ngày tạo/Thao tác = 8.
  const colSpan = tab === "counting" ? 7 : 8;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Yêu cầu điều chỉnh</h1>
        <p className="text-sm text-slate-500 mt-1">
          NV đề xuất trên app khi đếm nhầm / mất đồ tìm lại được, hoặc cần sửa đối soát nợ — duyệt tại đây.
        </p>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200">
        {(
          [
            ["counting", "Số lượng kiểm đếm"],
            ["debt", "Đối soát Nợ"],
          ] as [Tab, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`px-4 py-2.5 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px ${
              tab === value ? "border-green-600 text-green-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex justify-end">
        <div className="w-full sm:w-[220px]">
          <DropdownSelectOne
            defaultOption={{ value: "all", label: "Tất cả trạng thái" }}
            options={Object.entries(ADJUSTMENT_REQUEST_STATUS_LABEL).map(([value, label]) => ({
              value: Number(value),
              label,
            }))}
            selectedId={status === "all" ? "all" : status}
            onChange={(opt) => setStatus(opt.value === "all" ? "all" : Number(opt.value))}
            getOptionLabel={(o) => o.label}
            getOptionKey={(o) => o.value}
            placeholder="Trạng thái"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto overflow-y-auto max-h-[65vh] w-full">
        <table className="min-w-[900px] w-full text-sm text-slate-700 border-collapse">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0 z-10 text-center whitespace-nowrap">
            <tr>
              <th className="px-4 py-3 border border-slate-200">STT</th>
              {tab === "counting" ? (
                <>
                  <th className="px-4 py-3 border border-slate-200">Đơn</th>
                  <th className="px-4 py-3 border border-slate-200">Mốc</th>
                </>
              ) : (
                <>
                  <th className="px-4 py-3 border border-slate-200">Khách hàng</th>
                  <th className="px-4 py-3 border border-slate-200">Loại đồ</th>
                  <th className="px-4 py-3 border border-slate-200">Số lượng</th>
                </>
              )}
              <th className="px-4 py-3 border border-slate-200">Lý do</th>
              <th className="px-4 py-3 border border-slate-200">Trạng thái</th>
              <th className="px-4 py-3 border border-slate-200">Ngày tạo</th>
              <th className="px-4 py-3 sticky right-0 bg-slate-100 z-30 shadow-md border border-slate-200">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="py-6 text-center text-slate-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan}>
                  <EmptyState message="Chưa có yêu cầu nào" description="Không tìm thấy yêu cầu khớp bộ lọc." />
                </td>
              </tr>
            ) : (
              rows.map((r: any, idx: number) => (
                <tr key={r.id} className="border-t hover:bg-slate-50 transition group">
                  <td className="px-4 py-3 border border-slate-200 text-center">{(page - 1) * limit + idx + 1}</td>
                  {tab === "counting" ? (
                    <>
                      <td className="px-4 py-3 border border-slate-200 text-center">
                        {r.order_code || `#${r.order_id}`}
                      </td>
                      <td className="px-4 py-3 border border-slate-200 text-center">{STAGE_LABEL[r.stage as "counting" | "delivery_check"]}</td>
                    </>
                  ) : (
                    (() => {
                      const items = (r as DebtAdjustmentRequest).items;
                      const requestType = (r as DebtAdjustmentRequest).request_type || "debt";
                      return (
                        <>
                          <td className="px-4 py-3 border border-slate-200">
                            <div>{r.customer_name || `#${r.customer_id}`}</div>
                            {requestType !== "debt" && (
                              <span className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ${DEBT_TYPE_BADGE_STYLES[requestType]}`}>
                                {DEBT_REQUEST_TYPE_LABEL[requestType]}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 border border-slate-200">
                            {items.length === 0
                              ? "—"
                              : items.length === 1
                                ? items[0].category
                                : `${items[0].category} +${items.length - 1} loại khác`}
                          </td>
                          <td className="px-4 py-3 border border-slate-200 text-center">
                            {items.length === 0
                              ? "—"
                              : items.length === 1
                                ? (items[0].quantity >= 0 ? `+${items[0].quantity}` : items[0].quantity)
                                : `${items.length} dòng`}
                          </td>
                        </>
                      );
                    })()
                  )}
                  <td className="px-4 py-3 border border-slate-200 max-w-[240px] truncate" title={r.reason}>
                    {r.reason}
                  </td>
                  <td className="px-4 py-3 text-center border border-slate-200">
                    <span
                      className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap inline-block ${
                        STATUS_BADGE_STYLES[r.status] || "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {ADJUSTMENT_REQUEST_STATUS_LABEL[r.status] || "Không rõ"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center border border-slate-200">{formatDateTime(r.created_at)}</td>
                  <td className="px-4 py-3 text-center sticky right-0 bg-white group-hover:bg-slate-50 border border-slate-200">
                    <button
                      onClick={() => (tab === "counting" ? setSelectedCounting(r) : setSelectedDebt(r))}
                      className="px-3 py-1.5 text-xs font-semibold rounded-full border border-green-500 text-green-600 hover:bg-green-500 hover:text-white transition-all"
                    >
                      {r.status === ADJUSTMENT_REQUEST_STATUS.PENDING ? "Duyệt / Từ chối" : "Xem"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-500 font-medium">
          {totalRecords > 0
            ? `Hiển thị ${(page - 1) * limit + 1} - ${(page - 1) * limit + rows.length} trong tổng số ${totalRecords} yêu cầu`
            : "Chưa có dữ liệu"}
        </div>

        <div className="flex items-center gap-3">
          {/* Limit Select */}
          <select
            className="rounded-lg border-gray-200 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:ring-green-500 cursor-pointer"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={20}>20 hàng</option>
            <option value={50}>50 hàng</option>
            <option value={100}>100 hàng</option>
          </select>

          {/* Page Buttons */}
          <div className="flex items-center gap-1">
            <button
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" /></svg>
            </button>

            {(() => {
              const pages: (number | string)[] = [];
              const add = (p: number | string) => pages.push(p);
              const start = Math.max(1, page - 1);
              const end = Math.min(totalPages, page + 1);

              if (start > 1) add(1);
              if (start > 2) add("...");
              for (let p = start; p <= end; p++) add(p);
              if (end < totalPages - 1) add("...");
              if (end < totalPages) add(totalPages);

              return pages.map((p, idx) =>
                typeof p === "number" ? (
                  <button
                    key={idx}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold transition-all ${
                      p === page ? "bg-green-600 text-white shadow-md shadow-green-200" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ) : (
                  <span key={idx} className="px-1 text-gray-400 text-xs">...</span>
                ),
              );
            })()}

            <button
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>
            </button>
          </div>
        </div>
      </div>
      </div>

      <CountingAdjustmentDetailModal
        open={!!selectedCounting}
        request={selectedCounting}
        onClose={() => setSelectedCounting(null)}
        onUpdated={() => load()}
      />
      <DebtAdjustmentDetailModal
        open={!!selectedDebt}
        request={selectedDebt}
        onClose={() => setSelectedDebt(null)}
        onUpdated={() => load()}
      />
    </div>
  );
};

export default AdjustmentRequestIndexPage;
